import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Crop, Download, Info, Plus, X } from 'lucide-react'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router'
import { FormError } from '@/components/FormError'
import { PersonSelect } from '@/components/PersonSelect'
import { Avatar } from '@/components/tree/Avatar'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { organize, splitTags, type Organize, type Photo } from '@/lib/albums'
import { useTree } from '@/lib/tree'
import { cn } from '@/lib/utils'

const chip = 'inline-flex min-h-9 items-center gap-1.5 rounded-full bg-secondary px-3.5 text-sm font-medium'

// A photo's options, from the ••• under it: a sheet from the bottom on a phone. Whoever may change the photo edits its
// title, description, who's in it and its tags here; everyone else reads them. Either way it can be downloaded.
export function PhotoSheet({
  photo,
  name,
  open,
  onOpenChange,
  onDownload,
  onEdit,
}: {
  photo: Photo
  // What it's called when it has no title of its own.
  name: string
  open: boolean
  onOpenChange: (open: boolean) => void
  onDownload: () => void
  // Rotate, crop and color, for whoever may change the photo.
  onEdit: () => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-h-[85svh] gap-5 overflow-y-auto">
        {/* Remounted each time it opens, so the fields start from the photo as it is now. */}
        {open &&
          (photo.canEdit ? (
            <EditSheet photo={photo} onDone={() => onOpenChange(false)} onDownload={onDownload} onEdit={onEdit} />
          ) : (
            <ReadSheet photo={photo} name={name} onDownload={onDownload} />
          ))}
      </DialogContent>
    </Dialog>
  )
}

function ReadSheet({ photo, name, onDownload }: { photo: Photo; name: string; onDownload: () => void }) {
  return (
    <>
      <DialogTitle className="pr-8 text-xl font-semibold tracking-tight text-balance">
        {photo.title || name}
      </DialogTitle>
      {photo.caption && <p className="-mt-2 leading-snug text-pretty whitespace-pre-line">{photo.caption}</p>}
      {photo.people.length > 0 && (
        <section className="flex flex-col gap-2" aria-labelledby="sheet-people">
          <h3 id="sheet-people" className="eyebrow">
            In this photo
          </h3>
          <ul className="flex flex-wrap gap-2">
            {photo.people.map((p) => (
              <li key={p.id} className={cn(chip, 'pl-1')}>
                <Avatar person={p} className="size-7 text-xs shadow-none" />
                {p.name}
              </li>
            ))}
          </ul>
        </section>
      )}
      {photo.tags.length > 0 && (
        <ul className="flex flex-wrap gap-2" aria-label="Tags">
          {photo.tags.map((t) => (
            <li key={t} className={chip}>
              #{t}
            </li>
          ))}
        </ul>
      )}
      <Button variant="secondary" size="lg" onClick={onDownload}>
        <Download aria-hidden /> Download the original
      </Button>
    </>
  )
}

function EditSheet({
  photo,
  onDone,
  onDownload,
  onEdit,
}: {
  photo: Photo
  onDone: () => void
  onDownload: () => void
  onEdit: () => void
}) {
  const queryClient = useQueryClient()
  const tree = useTree(photo.familyId)
  const [title, setTitle] = useState(photo.title ?? '')
  const [caption, setCaption] = useState(photo.caption)
  const [typed, setTyped] = useState('')
  const refresh = async () => {
    for (const key of ['photo', 'related', 'stream', 'found-photos', 'album', 'person']) {
      await queryClient.invalidateQueries({ queryKey: [key] })
    }
  }
  // Who's in it and its tags change at once, as on the photo's own page.
  const tag = useMutation({
    mutationFn: (change: Organize) => organize(photo.familyId, [photo.id], change),
    onSuccess: refresh,
  })
  const save = useMutation({
    mutationFn: async () => {
      await api(`/album-photos/${photo.id}`, {
        method: 'PATCH',
        body: { title: title.trim(), caption: caption.trim() },
      })
      // Tags typed but not yet added go with the save.
      const tags = splitTags(typed)
      if (tags.length) await organize(photo.familyId, [photo.id], { addTags: tags })
    },
    onSuccess: async () => {
      await refresh()
      onDone()
    },
  })
  const untagged = (tree.data?.people ?? []).filter((p) => !photo.people.some((x) => x.id === p.id))
  const addTags = (e: FormEvent) => {
    e.preventDefault()
    const tags = splitTags(typed)
    if (tags.length) tag.mutate({ addTags: tags })
    setTyped('')
  }

  return (
    <>
      <DialogTitle className="text-xl font-semibold tracking-tight">Edit photo</DialogTitle>
      <form
        id="photo-sheet"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate()
        }}
      >
        <div className="grid gap-2">
          <Label htmlFor="sheet-title">Title</Label>
          <Input id="sheet-title" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="sheet-caption">Description</Label>
          <Textarea id="sheet-caption" maxLength={2000} value={caption} onChange={(e) => setCaption(e.target.value)} />
        </div>
      </form>

      {/* Left out when there's no one in the tree to tag. */}
      {(photo.people.length > 0 || untagged.length > 0) && (
        <section className="flex flex-col gap-2" aria-labelledby="sheet-people">
          <h3 id="sheet-people" className="eyebrow">
            In this photo
          </h3>
          {photo.people.length > 0 && (
            <ul className="flex flex-wrap gap-2">
              {photo.people.map((p) => (
                <li key={p.id} className={cn(chip, 'pr-1 pl-1')}>
                  <Avatar person={p} className="size-7 text-xs shadow-none" />
                  {p.name}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-8"
                    aria-label={`Untag ${p.name}`}
                    disabled={tag.isPending}
                    onClick={() => tag.mutate({ removePeople: [p.id] })}
                  >
                    <X aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          {untagged.length > 0 && (
            <PersonSelect
              aria-label="Tag someone in this photo"
              placeholder={tag.isPending ? 'Tagging…' : 'Tag someone'}
              people={untagged}
              value=""
              onChange={(id) => id && tag.mutate({ addPeople: [id] })}
            />
          )}
        </section>
      )}

      <section className="flex flex-col gap-2" aria-labelledby="sheet-tags">
        <h3 id="sheet-tags" className="eyebrow">
          Tags
        </h3>
        {photo.tags.length > 0 && (
          <ul className="flex flex-wrap gap-2">
            {photo.tags.map((t) => (
              <li key={t} className={cn(chip, 'pr-1')}>
                #{t}
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  aria-label={`Remove the tag ${t}`}
                  disabled={tag.isPending}
                  onClick={() => tag.mutate({ removeTags: [t] })}
                >
                  <X aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        )}
        <form className="flex gap-2" onSubmit={addTags}>
          <Input aria-label="Add tags" value={typed} onChange={(e) => setTyped(e.target.value)} />
          <Button type="submit" variant="outline" disabled={tag.isPending || splitTags(typed).length === 0}>
            <Plus aria-hidden /> Add
          </Button>
        </form>
      </section>
      <FormError error={tag.error ?? save.error} />

      <div className="flex flex-col gap-2">
        <Button type="submit" form="photo-sheet" size="lg" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button type="button" variant="secondary" size="lg" onClick={onEdit}>
          <Crop aria-hidden /> Rotate, crop and color
        </Button>
        <Button type="button" variant="secondary" size="lg" onClick={onDownload}>
          <Download aria-hidden /> Download the original
        </Button>
        {/* The date, who sees it and deleting are on the photo's own page. */}
        <Button asChild variant="ghost">
          <Link to={`/ph/${photo.id}`}>
            <Info aria-hidden /> More details
          </Link>
        </Button>
      </div>
    </>
  )
}
